<template>
  <div class="dashboard-container">
    <div class="page-header">
      <div class="row align-items-center">
        <div class="col">
          <h3 class="page-title">Dashboard - Ventas en Tiempo Real</h3>
        </div>
        <div class="col-auto">
          <div class="d-flex align-items-center">
            <span class="badge me-2" :class="socketConnected ? 'bg-success' : 'bg-danger'">
              <i class="fas fa-circle me-1"></i>
              {{ socketConnected ? 'Conectado' : 'Desconectado' }}
            </span>
            <button
              class="btn btn-sm btn-primary"
              @click="refreshVentas"
              :disabled="loading"
            >
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
              Actualizar
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="row mb-4">
      <div class="col-md-3">
        <div class="card stat-card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Ventas Hoy</h6>
                <h3 class="mb-0">{{ ventasHoy.length }}</h3>
              </div>
              <div class="stat-icon bg-primary">
                <i class="fas fa-shopping-cart"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-3">
        <div class="card stat-card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Total Hoy</h6>
                <h3 class="mb-0">${{ totalVentasHoy.toFixed(2) }}</h3>
              </div>
              <div class="stat-icon bg-success">
                <i class="fas fa-dollar-sign"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-3">
        <div class="card stat-card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Última Venta</h6>
                <h3 class="mb-0" v-if="ventasRecientes.length > 0">
                  ${{ ventasRecientes[0].total.toFixed(2) }}
                </h3>
                <h3 class="mb-0" v-else>-</h3>
              </div>
              <div class="stat-icon bg-info">
                <i class="fas fa-receipt"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-3">
        <div class="card stat-card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Promedio</h6>
                <h3 class="mb-0">${{ promedioVentas.toFixed(2) }}</h3>
              </div>
              <div class="stat-icon bg-warning">
                <i class="fas fa-chart-line"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="fas fa-list-ul me-2"></i>
              Ventas Recientes
            </h5>
          </div>
          <div class="card-body">
            <div v-if="loading && ventasRecientes.length === 0" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Cargando...</span>
              </div>
              <p class="mt-3 text-muted">Cargando ventas...</p>
            </div>

            <div v-else-if="error" class="alert alert-danger" role="alert">
              <i class="fas fa-exclamation-triangle me-2"></i>
              {{ error }}
            </div>

            <div v-else-if="ventasRecientes.length === 0" class="text-center py-5">
              <i class="fas fa-inbox fa-3x text-muted mb-3"></i>
              <p class="text-muted">No hay ventas registradas aún</p>
            </div>

            <div v-else class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>N° Venta</th>
                    <th>Cliente</th>
                    <th>Forma de Pago</th>
                    <th>Subtotal</th>
                    <th>Envío</th>
                    <th>Total</th>
                    <th>Total Pagado (Bs)</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="venta in ventasRecientes"
                    :key="venta.id"
                    :class="{ 'table-success': isNewVenta(venta.id) }"
                  >
                    <td>
                      <strong>{{ venta.nventa }}</strong>
                      <span v-if="isNewVenta(venta.id)" class="badge bg-success ms-2">
                        <i class="fas fa-star"></i> Nuevo
                      </span>
                    </td>
                    <td>
                      <div v-if="venta.cliente">
                        {{ venta.cliente.nombre }} {{ venta.cliente.apellido }}
                      </div>
                      <small class="text-muted" v-if="venta.cliente">
                        {{ venta.cliente.email }}
                      </small>
                    </td>
                    <td>
                      <span class="badge" :class="getPaymentBadgeClass(venta.forma_pago)">
                        {{ venta.forma_pago }}
                      </span>
                    </td>
                    <td>${{ venta.subtotal.toFixed(2) }}</td>
                    <td>${{ venta.envio.toFixed(2) }}</td>
                    <td>
                      <strong class="text-success">${{ venta.total.toFixed(2) }}</strong>
                    </td>
                    <td>
                      <strong class="text-info">{{ formatoMonedaBs(venta.total_pagado) }}</strong>
                    </td>
                    <td>
                      <span class="badge" :class="getStatusBadgeClass(venta.estado)">
                        {{ venta.estado }}
                      </span>
                    </td>
                    <td>
                      <small>{{ formatDate(venta.createdAt) }}</small>
                    </td>
                    <td>
                      <button
                        class="btn btn-sm btn-outline-primary"
                        @click="verDetalle(venta)"
                        data-bs-toggle="modal"
                        data-bs-target="#modalDetalleVenta">
                        <i class="fas fa-eye"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Detalle de Venta -->
    <div class="modal fade" id="modalDetalleVenta" tabindex="-1" aria-labelledby="modalDetalleVentaLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="modalDetalleVentaLabel">
              <i class="fas fa-receipt me-2"></i>
              Detalle de Venta {{ ventaSeleccionada?.nventa }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" v-if="ventaSeleccionada">
            <!-- Información General -->
            <div class="row mb-4">
              <div class="col-md-6">
                <h6 class="text-muted mb-3">Información de la Venta</h6>
                <p class="mb-2"><strong>N° Venta:</strong> <span class="badge bg-primary">{{ ventaSeleccionada.nventa }}</span></p>
                <p class="mb-2"><strong>Fecha:</strong> {{ formatearFecha(ventaSeleccionada.createdAt) }} {{ formatearHora(ventaSeleccionada.createdAt) }}</p>
                <p class="mb-2"><strong>Estado:</strong> <span class="badge" :class="getEstadoBadge(ventaSeleccionada.estado)">{{ ventaSeleccionada.estado }}</span></p>
                <p class="mb-2"><strong>Método de Pago:</strong> <span class="badge" :class="getMetodoPagoBadge(ventaSeleccionada.forma_pago)">{{ ventaSeleccionada.forma_pago }}</span></p>
                <p class="mb-2" v-if="ventaSeleccionada.transaccion"><strong>N° Transacción:</strong> {{ ventaSeleccionada.transaccion }}</p>
                <p class="mb-2" v-if="ventaSeleccionada.bancoId"><strong>Banco:</strong> {{ ventaSeleccionada.banco.nombre }}</p>
              </div>
              <div class="col-md-6">
                <h6 class="text-muted mb-3">Información del Cliente</h6>
                <p class="mb-2"><strong>Nombre:</strong> {{ ventaSeleccionada.cliente?.nombres }} {{ ventaSeleccionada.cliente?.apellidos }}</p>
                <p class="mb-2"><strong>Email:</strong> {{ ventaSeleccionada.cliente?.email }}</p>
                <p class="mb-2" v-if="ventaSeleccionada.cliente?.genero"><strong>Género:</strong> {{ ventaSeleccionada.cliente.genero }}</p>
                <p class="mb-2" v-if="ventaSeleccionada.cliente?.pais"><strong>País:</strong> {{ ventaSeleccionada.cliente.pais }}</p>
              </div>
            </div>

            <!-- Información de Dirección de Envío -->
            <div class="row mb-4" v-if="ventaSeleccionada.direccion">
              <div class="col-12">
                <h6 class="text-muted mb-3">Dirección de Envío</h6>
                <div class="card bg-light">
                  <div class="card-body">
                    <p class="mb-2"><strong>Dirección:</strong> {{ ventaSeleccionada.direccion.direccion }}</p>
                    <p class="mb-2"><strong>Teléfono:</strong> {{ ventaSeleccionada.direccion.telefono }}</p>
                    <div class="row">
                      <div class="col-md-4">
                        <p class="mb-2"><strong>Ciudad:</strong> {{ ventaSeleccionada.direccion.ciudad }}</p>
                      </div>
                      <div class="col-md-4">
                        <p class="mb-2"><strong>País:</strong> {{ ventaSeleccionada.direccion.pais }}</p>
                      </div>
                      <div class="col-md-4">
                        <p class="mb-2"><strong>Código Postal:</strong> {{ ventaSeleccionada.direccion.zip }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Productos -->
            <h6 class="text-muted mb-3">Productos</h6>
            <div class="table-responsive mb-4">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                  <tr>
                    <th>Producto</th>
                    <th>SKU</th>
                    <th>Variedad</th>
                    <th class="text-center">Cantidad</th>
                    <th class="text-end">Precio Unit.</th>
                    <th class="text-end">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="detalle in ventaSeleccionada.detalles" :key="detalle.id">
                    <td>
                      <div class="fw-semibold">{{ detalle.producto?.titulo || `Producto #${detalle.productoId}` }}</div>
                      <small class="text-muted">{{ detalle.producto?.slug }}</small>
                    </td>
                    <td>
                      <span v-if="detalle.variedad?.sku" class="badge bg-secondary">{{ detalle.variedad.sku }}</span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <div v-if="detalle.variedad">
                        <span v-if="detalle.variedad.talla" class="badge bg-info me-1">{{ detalle.variedad.talla }}</span>
                        <span v-if="detalle.variedad.color" class="badge bg-success me-1">{{ detalle.variedad.color }}</span>
                        <span v-if="detalle.variedad.medida" class="badge bg-warning">{{ detalle.variedad.medida }}</span>
                      </div>
                      <span v-else class="text-muted">Sin variedad</span>
                    </td>
                    <td class="text-center">{{ detalle.cantidad }}</td>
                    <td class="text-end">{{ formatoMoneda(detalle.precio_unidad) }}</td>
                    <td class="text-end fw-semibold">{{ formatoMoneda(detalle.cantidad * detalle.precio_unidad) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Totales -->
            <div class="row">
              <div class="col-md-6 offset-md-6">
                <table class="table table-sm">
                  <tr>
                    <td class="text-end"><strong>Subtotal:</strong></td>
                    <td class="text-end">{{ formatoMoneda(ventaSeleccionada.subtotal) }}</td>
                  </tr>
                  <tr>
                    <td class="text-end"><strong>Envío:</strong></td>
                    <td class="text-end">{{ formatoMoneda(ventaSeleccionada.envio) }}</td>
                  </tr>
                  <tr class="table-primary">
                    <td class="text-end"><strong>Total:</strong></td>
                    <td class="text-end"><strong>{{ formatoMoneda(ventaSeleccionada.total) }}</strong></td>
                  </tr>
                  <tr class="table-success">
                    <td class="text-end"><strong>Total Pagado (Bs):</strong></td>
                    <td class="text-end"><strong>{{ formatoMonedaBs(ventaSeleccionada.total_pagado) }}</strong></td>
                  </tr>
                </table>
              </div>
            </div>

            <!-- Cambiar Estado -->
            <div class="row mt-4" v-if="ventaSeleccionada.estado !== 'Entregado' && ventaSeleccionada.estado !== 'Procesado'">
              <div class="col-12">
                <h6 class="text-muted mb-3">Cambiar Estado</h6>
                <div class="d-flex gap-2">
                  <button
                    v-if="ventaSeleccionada.estado === 'Pendiente'"
                    class="btn btn-warning"
                    @click="cambiarEstado('En Proceso')"
                    :disabled="loading">
                    <i class="fas fa-clock me-2"></i>
                    Marcar como En Proceso
                  </button>
                  <button
                    v-if="ventaSeleccionada.estado === 'En Proceso' || ventaSeleccionada.estado === 'Pendiente'"
                    class="btn btn-success"
                    @click="cambiarEstado('Entregado')"
                    :disabled="loading">
                    <i class="fas fa-truck me-2"></i>
                    Marcar como Entregado
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary m-1" data-bs-dismiss="modal">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import api from '@/api';
import socketService from '@/utils/socket'; // Importación de la instancia

export default {
  name: 'Dashboard',
  setup() {
    const ventasRecientes = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const socketConnected = ref(false);
    const newVentasIds = ref(new Set());
    const ventaSeleccionada = ref(null);

    // Computed properties
    const ventasHoy = computed(() => {
      const hoy = new Date().toDateString();
      return ventasRecientes.value.filter(venta => {
        // NOTA: Si usas year/month/day en el backend, esta lógica de filtro
        // en el frontend es redundante si solo se trae ventas de hoy.
        const ventaDate = new Date(venta.createdAt).toDateString();
        return ventaDate === hoy;
      });
    });

    const totalVentasHoy = computed(() => {
      return ventasHoy.value.reduce((sum, venta) => sum + parseFloat(venta.total), 0);
    });

    const promedioVentas = computed(() => {
      if (ventasHoy.value.length === 0) return 0;
      return totalVentasHoy.value / ventasHoy.value.length;
    });

    // Methods
    const fetchVentas = async () => {
      try {
        loading.value = true;
        error.value = null;
        // Asumiendo que esta ruta usa el controlador getVentasDiaAdmin corregido
        const response = await api.get('/venta/day'); 

        // Ordenar por fecha más reciente primero
        ventasRecientes.value = response.data.sort((a, b) => {
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
      } catch (err) {
        // Manejo de errores
        // ... (Tu manejo de errores existente)
        error.value = 'Error al cargar las ventas';
      } finally {
        loading.value = false;
      }
    };

    const refreshVentas = () => {
      fetchVentas();
    };

    const isNewVenta = (ventaId) => {
      return newVentasIds.value.has(ventaId);
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };
    
    // ... (getPaymentBadgeClass, getStatusBadgeClass, verDetalle se mantienen igual)
    const getPaymentBadgeClass = (formaPago) => {
        const classes = { 'transferencia': 'bg-info', 'efectivo': 'bg-success', 'tarjeta': 'bg-primary', 'yape': 'bg-warning text-dark', 'plin': 'bg-danger' };
        return classes[formaPago] || 'bg-secondary';
    };

    const getStatusBadgeClass = (estado) => {
        const classes = { 'Pendiente': 'bg-warning text-dark', 'Procesando': 'bg-info', 'Enviado': 'bg-primary', 'Entregado': 'bg-success', 'Cancelado': 'bg-danger' };
        return classes[estado] || 'bg-secondary';
    };

    const verDetalle = async (venta) => {
      try {
        const response = await api.get(`/venta/admin/${venta.id}`);
        ventaSeleccionada.value = response.data;
      } catch (err) {
        window.Swal.fire({
          title: 'Error',
          text: 'No se pudo cargar el detalle de la venta',
          icon: 'error'
        });
      }
    };

    const cambiarEstado = async (nuevoEstado) => {
      const result = await window.Swal.fire({
        title: '¿Cambiar Estado?',
        text: `¿Está seguro de cambiar el estado a "${nuevoEstado}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, cambiar',
        cancelButtonText: 'Cancelar'
      });

      if (!result.isConfirmed) return;

      try {
        await api.get(`/venta/admin/cambiar-estado/${ventaSeleccionada.value.id}/${nuevoEstado}`);

        ventaSeleccionada.value.estado = nuevoEstado;

        await fetchVentas();

        window.Swal.fire({
          title: 'Estado actualizado',
          text: `El estado se actualizó a "${nuevoEstado}"`,
          icon: 'success',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true
        });
      } catch (err) {
        window.Swal.fire({
          title: 'Error',
          text: 'No se pudo cambiar el estado de la venta',
          icon: 'error'
        });
      }
    };

    const formatoMoneda = (value) => {
      if (!value) return '$0,00';
      const numero = Number(value).toFixed(2);
      return '$' + numero
        .replace('.', ',')
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    };

    const formatoMonedaBs = (value) => {
      if (!value) return 'Bs 0,00';
      const numero = Number(value).toFixed(2);
      return 'Bs ' + numero
        .replace('.', ',')
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    };

    const formatearFecha = (fecha) => {
      const date = new Date(fecha);
      const dia = String(date.getDate()).padStart(2, '0');
      const mes = String(date.getMonth() + 1).padStart(2, '0');
      const anio = date.getFullYear();
      return `${dia}/${mes}/${anio}`;
    };

    const formatearHora = (fecha) => {
      const date = new Date(fecha);
      const horas = String(date.getHours()).padStart(2, '0');
      const minutos = String(date.getMinutes()).padStart(2, '0');
      return `${horas}:${minutos}`;
    };

    const getEstadoBadge = (estado) => {
      const estadoLower = (estado || '').toLowerCase();
      const badges = {
        'completada': 'bg-success',
        'completado': 'bg-success',
        'entregado': 'bg-success',
        'procesado': 'bg-success',
        'pendiente': 'bg-warning',
        'en proceso': 'bg-info',
        'anulada': 'bg-danger',
        'anulado': 'bg-danger',
        'cancelada': 'bg-danger',
        'cancelado': 'bg-danger'
      };
      return badges[estadoLower] || 'bg-secondary';
    };

    const getMetodoPagoBadge = (metodo) => {
      const metodoLower = (metodo || '').toLowerCase();
      const badges = {
        'efectivo': 'bg-success',
        'tarjeta': 'bg-primary',
        'transferencia': 'bg-info',
        'transferencia bancaria': 'bg-info',
        'pago_movil': 'bg-warning',
        'pago movil': 'bg-warning',
        'pago móvil': 'bg-warning',
        'punto de venta': 'bg-primary'
      };
      return badges[metodoLower] || 'bg-secondary';
    };


    const handleNuevaVenta = (data) => {
      console.log('🛒 Nueva venta recibida en tiempo real:', data);
      ventasRecientes.value.unshift(data.venta);
      newVentasIds.value.add(data.venta.id);

      // Mostrar notificación (usando window.Swal)
      window.Swal.fire({
        // ... (Tu configuración de Swal)
        title: '¡Nueva Venta!',
        icon: 'success',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true
      });

      // Reproducir sonido (opcional)
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZVRE');
        audio.play().catch(e => console.log('No se pudo reproducir el sonido:', e));
      } catch (e) {
        console.log('Audio no disponible');
      }

      // Remover el badge "Nuevo" después de 10 segundos
      setTimeout(() => {
        newVentasIds.value.delete(data.venta.id);
      }, 10000);
    };

    // Lifecycle hooks
    onMounted(() => {
      // Cargar ventas iniciales
      fetchVentas();

      // Intentar conectar al WebSocket
      try {
        socketService.connect();

        // Verificar conexión después de un tiempo
        setTimeout(() => {
          // CLAVE: Usamos el método isConnected() en la instancia importada.
          socketConnected.value = socketService.isConnected(); 

          if (socketConnected.value) {
            console.log('✅ WebSocket conectado - Recibirás notificaciones en tiempo real');
          } else {
            console.log('ℹ️ WebSocket no disponible - Usa el botón "Actualizar" para ver nuevas ventas');
          }
        }, 2000);

        // Escuchar nuevas ventas (solo si el socket está inicializado)
        if (socketService.socket) {
          socketService.onNuevaVenta(handleNuevaVenta);
        }
      } catch (error) {
        console.warn('⚠️ No se pudo conectar al WebSocket:', error);
        socketConnected.value = false;
      }
    });

    onUnmounted(() => {
      // Dejar de escuchar eventos
      socketService.offNuevaVenta();
      // NOTA: Se mantiene la conexión abierta, si quieres cerrarla usa socketService.disconnect();
    });

    return {
      ventasRecientes,
      loading,
      error,
      socketConnected,
      ventaSeleccionada,
      ventasHoy,
      totalVentasHoy,
      promedioVentas,
      refreshVentas,
      isNewVenta,
      formatDate,
      getPaymentBadgeClass,
      getStatusBadgeClass,
      verDetalle,
      cambiarEstado,
      formatoMoneda,
      formatoMonedaBs,
      formatearFecha,
      formatearHora,
      getEstadoBadge,
      getMetodoPagoBadge
    };
  }
};
</script>

<style scoped>
.dashboard-container { padding: 0; }
.page-header { margin-bottom: 1.5rem; }
.stat-card { border: none; box-shadow: 0 0 20px rgba(0, 0, 0, 0.08); transition: transform 0.3s ease; }
.stat-card:hover { transform: translateY(-5px); }
.stat-icon { width: 60px; height: 60px; border-radius: 12px; display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; }
.table-success { animation: highlight 2s ease; }
@keyframes highlight { 0% { background-color: #d4edda; } 100% { background-color: transparent; } }
.card { border: none; box-shadow: 0 0 20px rgba(0, 0, 0, 0.08); }
.card-header { background-color: #fff; border-bottom: 1px solid #e9ecef; padding: 1rem 1.5rem; }
.table thead th { border-top: none; background-color: #f8f9fa; font-weight: 600; font-size: 0.875rem; text-transform: uppercase; letter-spacing: 0.5px; color: #6c757d; }
.table tbody tr { transition: background-color 0.2s ease; }
.table tbody tr:hover { background-color: #f8f9fa; }
.badge { font-weight: 500; padding: 0.35em 0.65em; }
</style>